<template>
  <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel()">
    <div class="flex flex-wrap gap-4">
      <div class="flex flex-col flex-1">
        <h2 class="text-base text-gray-600">Negocios</h2>

        <Dropdown
          v-model="businessSelected"
          :options="businesses"
          class="w-full md:w-14rem"
          optionLabel="name"
          placeholder="Selecciona el negocio"
          @change="getWabaId"
        />
        <small class="p-error">{{
          $t(
            'INBOX_MGMT.WHATSAPP_TEMPLATES.ADD_TEMPLATE_PAGE.FORM.CATEGORY.ERROR'
          )
        }}</small>
      </div>
    </div>
    <div class="flex flex-wrap gap-4">
      <div class="flex flex-col flex-1">
        <h2 class="text-base text-gray-600">WABA</h2>

        <Dropdown
          v-model="wabaSelected"
          :options="wabas"
          class="w-full md:w-14rem"
          optionLabel="name"
          placeholder="Selecciona el WABA"
          @change="getPhoneNumbers"
        />
        <small class="p-error">{{
          $t(
            'INBOX_MGMT.WHATSAPP_TEMPLATES.ADD_TEMPLATE_PAGE.FORM.CATEGORY.ERROR'
          )
        }}</small>
      </div>
    </div>
    <div class="flex flex-wrap gap-4">
      <div class="flex flex-col flex-1">
        <h2 class="text-base text-gray-600">Números de teléfono</h2>

        <Dropdown
          v-model="phoneNumberSelected"
          :options="phoneNumbers"
          class="w-full md:w-14rem"
          optionLabel="display_phone_number"
          placeholder="Selecciona el número de teléfono"
        />
        <small class="p-error">{{
          $t(
            'INBOX_MGMT.WHATSAPP_TEMPLATES.ADD_TEMPLATE_PAGE.FORM.CATEGORY.ERROR'
          )
        }}</small>
      </div>
    </div>
    <div class="w-full mt-4">
      <NextButton
        :is-loading="uiFlags.isCreating"
        type="submit"
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
      />
    </div>
  </form>
</template>
<script setup>
import { watch, ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../../../index';
import { isPhoneE164OrEmpty, isNumber } from 'shared/helpers/Validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const store = useStore();
const { t } = useI18n();

// Computed para obtener `uiFlags` desde el store
const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const rules = {
  inboxName: { required },
  phoneNumber: { required, isPhoneE164OrEmpty },
  apiKey: { required },
  phoneNumberId: { required, isNumber },
  businessAccountId: { required, isNumber },
};

const businessSelected = ref(null);
const businesses = ref([]);
const wabaSelected = ref(null);
const wabas = ref([]);
const phoneNumberSelected = ref(null);
const phoneNumbers = ref([]);
const newLongAliveToken = ref(null);
const props = defineProps({
  authResponse: {
    type: Object,
    required: true,
  },
});

const v$ = useVuelidate(rules, {
  inboxName: phoneNumberSelected
    ? phoneNumberSelected.value?.verified_name
    : null,
  phoneNumber: phoneNumberSelected
    ? phoneNumberSelected.value?.display_phone_number
    : null,
  apiKey: newLongAliveToken?.value,
  phoneNumberId: phoneNumberSelected ? phoneNumberSelected.value?.id : null,
  businessAccountId: wabaSelected ? wabaSelected.value?.id : null,
});

const getBusiness = async accessToken => {
  const response = await fetch(
    `https://graph.facebook.com/v19.0/me/businesses?access_token=${accessToken}`
  );
  const data = await response.json();
  if (!data.data || data.data.length === 0) {
    throw new Error('No se encontró ningún Business ID asociado.');
  }
  console.log('Business ID data:', data.data);
  businesses.value = data.data;
  return data.data[0].id; // Retorna el Business ID
};

const getWabaId = async () => {
  const response = await fetch(
    `https://graph.facebook.com/v19.0/${businessSelected.value.id}/owned_whatsapp_business_accounts?access_token=${props.authResponse.token}`
  );
  const data = await response.json();
  if (!data.data || data.data.length === 0) {
    console.log('WABA ID data:', data.data);
    throw new Error('No se encontró ningún WABA ID asociado.');
  }
  wabas.value = data.data;
  console.log('WABA ID data:', data.data);
  return data.data[0].id; // Retorna el WABA ID
};

const getPhoneNumbers = async wabaId => {
  const response = await fetch(
    `https://graph.facebook.com/v19.0/${wabaSelected.value.id}/phone_numbers?access_token=${props.authResponse.token}`
  );
  const data = await response.json();
  if (!data.data) {
    console.log('Phone ID data:', data.data);
    throw new Error('No se encontraron números de teléfono asociados.');
  }
  phoneNumbers.value = data.data;
  return data; // Retorna los números asociados
};

async function getWhatsAppIds(authResponse) {
  try {
    const { userID, token } = authResponse;

    // 1. Obtener WABA ID
    const wabaId = await getWabaId(userID, token);

    // 2. Obtener números de teléfono
    const phoneData = await getPhoneNumbers(wabaId, token);

    return {
      businessManagerId: userID,
      wabaId,
      phoneNumbers: phoneData.data,
    };
  } catch (error) {
    console.error('Error obteniendo IDs:', error);
    throw error;
  }
}

const phone_number = ref(null);
const phone_number_id = ref(null);
const waba_id = ref(null);
const token_response = ref(null);

async function createChannel() {
  /*v$.$touch();
  if (v$.$invalid) {
    return;
  }*/

  try {
    const whatsappChannel = await store.dispatch('inboxes/createChannel', {
      name: "Testeo",
      channel: {
        type: 'whatsapp',
        phone_number: phoneNumberSelected?.value.display_phone_number
          .toString()
          .trim(),
        provider: 'whatsapp_cloud',
        provider_config: {
          api_key: newLongAliveToken.value,
          phone_number_id: phoneNumberSelected?.value.id,
          business_account_id: wabaSelected?.value.id,
        },
      },
    });
    if (whatsappChannel) {
      const pin = Math.floor(100000 + Math.random() * 900000);
      const verificationPhone = verifyPhoneNumber(
        phoneNumberSelected?.value.id,
        newLongAliveToken.value,
        pin.toString().trim()
      );
      console.log('verificationPhone', verificationPhone);
    }
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: whatsappChannel.id,
      },
    });
  } catch (error) {
    useAlert(error.message || $t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE'));
  }
}

async function verifyPhoneNumber(phoneNumberId, accessToken, pin) {
  const url = `https://graph.facebook.com/v22.0/${phoneNumberId}/register`;

  const payload = {
    messaging_product: 'whatsapp',
    pin: pin, // Código de 6 dígitos recibido por SMS o llamada
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        `Error al registrar el número: ${
          data.error?.message || 'Error desconocido'
        }`
      );
    }

    console.log('Número registrado exitosamente:', data);
    return data;
  } catch (error) {
    console.error('Error en la verificación del número:', error.message);
    throw error;
  }
}

watch(
  () => props.authResponse, // Observa el prop `authResponse`
  async (newValue, oldValue) => {
    console.log('authResponse cambió:', { oldValue, newValue });
    if (newValue) {
      const { userID, token } = newValue;
      newLongAliveToken.value = await exchangeToken(token);
      // Lógica adicional cuando `authResponse` cambia
      getBusiness(newLongAliveToken.value);
    }
  },
  { immediate: true } // Ejecuta el watcher inmediatamente con el valor inicial
);

async function exchangeToken(shortLivedToken) {
  const clientId = '1087562195602449'; // Tu App ID
  const clientSecret = '5d830afce050493372a06cff242a62c9'; // Tu App Secret

  const url = `https://graph.facebook.com/v19.0/oauth/access_token?grant_type=fb_exchange_token&client_id=${clientId}&client_secret=${clientSecret}&fb_exchange_token=${shortLivedToken}`;

  const response = await fetch(url, { method: 'GET' });
  const data = await response.json();

  if (data.error) {
    throw new Error(`Error intercambiando token: ${data.error.message}`);
  }

  return data.access_token; // Token de larga duración
}

window.addEventListener('message', event => {
  if (
    event.origin !== 'https://www.facebook.com' &&
    event.origin !== 'https://web.facebook.com'
  ) {
    return;
  }
  try {
    const data = JSON.parse(event.data);
    if (data.type === 'WA_EMBEDDED_SIGNUP') {
      // if user finishes the Embedded Signup flow
      if (data.event === 'FINISH') {
        phone_number_id.value = data.data.phone_number_id;
        phone_number.value = data.data.display_phone_number.toString().trim();
        waba_id.value = data.data.waba_id;
        
        console.log('Data :::: ', data.data);
        // if user cancels the Embedded Signup flow
      } else if (data.event === 'CANCEL') {
        const { current_step } = data.data;
        console.warn('Cancel at ', current_step);
        // if user reports an error during the Embedded Signup flow
      } else if (data.event === 'ERROR') {
        const { error_message } = data.data;
        console.error('error ', error_message);
      }
    }
    document.getElementById('session-info-response').textContent =
      JSON.stringify(data, null, 2);
  } catch {
    console.log('Non JSON Responses', event.data);
  }
});

const fbLoginCallback = response => {
  if (response.authResponse) {
    const code = response.authResponse.code;
    console.log('response.authResponse :::::::: ', code);
    token_response.value = code;
    // The returned code must be transmitted to your backend first and then
    // perform a server-to-server call from there to our servers for an access token.
  }
  document.getElementById('sdk-response').textContent = JSON.stringify(
    response,
    null,
    2
  );
};

const launchWhatsAppSignup = () => {
  // Launch Facebook login
  FB.login(fbLoginCallback, {
    config_id: '1204109103916969', // configuration ID goes here
    response_type: 'code', // must be set to 'code' for System User access token
    override_default_response_type: true, // when true, any response types passed in the "response_type" will take precedence over the default types
    extras: {
      setup: {},
      featureType: '',
      sessionInfoVersion: '2',
    },
  });
};

window.fbAsyncInit = function () {
  FB.init({
    appId: '1087562195602449',
    autoLogAppEvents: true,
    xfbml: true,
    version: 'v22.0',
  });
};
</script>